import { useContentfulLiveUpdates } from '@contentful/live-preview/react';
import { ContentfulLivePreview } from '@contentful/live-preview';
import {
    type BrandDetailedModel,
    type ContentfulArticleGroupModel,
    type ContentfulArticleSearchResponse,
    type ContentfulContentModel,
    ContentfulCustomDataFieldDiscriminator,
    type ContentfulCustomDataModel,
    type ContentfulPage,
    type ContentfulPageActionDataField,
    type ContentfulPageModel,
    type ContentfulSectionMenuDataField,
    GetContentfulContentPreviewData,
    GetContentfulPagePreviewData,
    type ProductListItemModel
} from 'autogen/swagger/all';
import type { Entry } from 'contentful';
import resolveResponse from 'contentful-resolve-response';
import { useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import type { TypeStandardTemplateDetailed, TypeWebpageDetailed } from 'types/autogen/contentful';
import checkRecursion from '../helpers/checkForResursion';

type ResolvedContentfulLiveUpdatesResult = {
    resolvedData: TypeWebpageDetailed[] | TypeStandardTemplateDetailed[];
    hasRecursion?: boolean;
};
type WebpageDetailedResult = Omit<ResolvedContentfulLiveUpdatesResult, 'resolvedData'> & {
    resolvedData: TypeWebpageDetailed[];
};
type StandardTemplateDetailedResult = Omit<ResolvedContentfulLiveUpdatesResult, 'resolvedData'> & {
    resolvedData: TypeStandardTemplateDetailed[];
};

function replaceFields(entry: Entry, customDataModel: ContentfulCustomDataModel) {
    const id = `id_${entry.sys.id}`;

    const dataField = customDataModel?.fieldData[id];

    if (!dataField) return;

    for (const field of dataField) {
        const key = field.fieldName;

        const dataForField = ContentfulCustomDataFieldDiscriminator<any[]>(
            field.customDataField,
            (model) => model.productsListItems as ProductListItemModel[],
            (model) => model.pageActions as ContentfulPageActionDataField['pageActions'],
            (model) => model.brandDetailedModels as BrandDetailedModel[],
            (model) => [model.searchResponseModel as ContentfulArticleSearchResponse],
            (model) => [model.articleGroup as ContentfulArticleGroupModel],
            (model) => [model as ContentfulSectionMenuDataField],
            () => []
        );

        if (field.isArray) {
            entry.fields[key] = dataForField;
        } else {
            entry.fields[key] = dataForField[0];
        }
    }
}

export default function resolveContentfulResponse(data: string, customDataModel: ContentfulCustomDataModel) {
    const dataObject = JSON.parse(data);

    dataObject.items.forEach((item: Entry) => replaceFields(item, customDataModel));
    dataObject.includes?.Entry?.forEach((item: Entry) => replaceFields(item, customDataModel));

    const result = resolveResponse(dataObject);

    return result;
}

function isObject(item) {
    return item && typeof item === 'object' && !Array.isArray(item);
}

function deepResolve(liveUpdates, customDataModel: ContentfulCustomDataModel) {
    if (Array.isArray(liveUpdates)) {
        for (const liveUpdate of liveUpdates) {
            deepResolve(liveUpdate, customDataModel);
        }
    } else if (isObject(liveUpdates)) {
        if ('fields' in liveUpdates) {
            replaceFields(liveUpdates, customDataModel);
            for (const key in liveUpdates.fields) {
                deepResolve(liveUpdates.fields[key], customDataModel);
            }
        } else {
            if ('content' in liveUpdates) {
                deepResolve(liveUpdates.content, customDataModel);
            }
            if ('data' in liveUpdates) {
                deepResolve(liveUpdates.data, customDataModel);
            }
            if ('target' in liveUpdates) {
                deepResolve(liveUpdates.target, customDataModel);
            }
        }
    }

    return liveUpdates;
}

export function useResolvedContentfulPageLiveUpdates(model: ContentfulPageModel): WebpageDetailedResult {
    return useResolvedContentfulLiveUpdates(
        model.contentfulPage.data,
        model.customDataModel,
        model.isPreview,
        async () => {
            const result = await GetContentfulPagePreviewData({ slug: model.contentfulPage?.slug });
            return { data: result.contentfulPage.data, customDataModel: result.customDataModel };
        },
        model.contentfulLocale
    ) as WebpageDetailedResult;
}

export function useResolvedContentfulContentLiveUpdates(model: ContentfulContentModel): StandardTemplateDetailedResult {
    return useResolvedContentfulLiveUpdates(
        model.contentfulContent.data,
        model.customDataModel,
        model.isPreview,
        async () => {
            const result = await GetContentfulContentPreviewData({
                webshopId: model.contentfulContent?.contentfulWebshopId
            });
            return { data: result.contentfulContent.data, customDataModel: result.customDataModel };
        }
    ) as StandardTemplateDetailedResult;
}

function useSafeContentfulLiveUpdates(
    resolved: any,
    isPreview: boolean
): ReturnType<typeof useContentfulLiveUpdates> | null {
    try {
        return isPreview ? useContentfulLiveUpdates(resolved) : null;
    } catch (err) {
        console.warn('Skipping live updates because ContentfulLivePreview is not initialized.', err);
        return null;
    }
}

function useResolvedContentfulLiveUpdates(
    data: ContentfulPage['data'],
    customDataModel: ContentfulCustomDataModel,
    isPreview: boolean,
    previewDataFunction: () => Promise<{ data: ContentfulPage['data']; customDataModel: ContentfulCustomDataModel }>,
    locale: string | undefined = undefined
): ResolvedContentfulLiveUpdatesResult {
    const JsonData = JSON.parse(data);
    const [hasRecursion, setHasRecursion] = useState(() => checkRecursion(JsonData.items[0], JsonData.includes));
    const [resolvedData] = useState(() => resolveContentfulResponse(data, customDataModel));
    const [resolved] = useState(() => resolveResponse(JsonData));
    const [previewCustomDataModel, setPreviewCustomDataModel] = useState(customDataModel);
    const [resolvedPreviewData, setResolvedPreviewData] = useState<
        ResolvedContentfulLiveUpdatesResult['resolvedData'] | null
    >(null);
    const liveUpdates = useSafeContentfulLiveUpdates(resolved, isPreview);
    const lastRequest = useRef('');
    const lastRequestTime = useRef(new Date());

    useEffect(() => {
        if (!isPreview) return;

        return ContentfulLivePreview.subscribe('edit', {
            data: resolved,
            locale,
            callback: async () => {
                if (new Date().getTime() - lastRequestTime.current.getTime() < 100) {
                    console.warn('Too many calls to Contentful too fast');
                    return;
                }

                lastRequestTime.current = new Date();
                const thisRequest = uuidv4();
                lastRequest.current = thisRequest;

                const { data: newData, customDataModel: newCustomDataModel } = await previewDataFunction();
                if (!newData || !newCustomDataModel) return;
                if (lastRequest.current !== thisRequest) return;

                const newJsonData = JSON.parse(newData);
                setHasRecursion(checkRecursion(newJsonData.items[0], newJsonData.includes));
                setPreviewCustomDataModel(newCustomDataModel);
            }
        });
    }, [isPreview, data]);

    useEffect(() => {
        if (!isPreview || hasRecursion || !liveUpdates) return;

        const resolved = deepResolve(JSON.parse(JSON.stringify(liveUpdates)), previewCustomDataModel);
        setResolvedPreviewData(resolved);
    }, [isPreview, hasRecursion, liveUpdates, previewCustomDataModel]);

    if (!isPreview || hasRecursion || resolvedPreviewData === null) {
        return { resolvedData, hasRecursion };
    }

    return { resolvedData: resolvedPreviewData, hasRecursion };
}
